type ThemeStyleValue = string | number | ThemeStyleSpec | null;
type ThemeStyleSpec = { [selector: string]: ThemeStyleValue };

export const CM_WIDGET_FONT_FAMILY = "ui-sans-serif, system-ui, sans-serif";
export const CM_MONO_FONT_FAMILY =
    "ui-monospace, 'SF Mono', Menlo, Monaco, monospace";

export function directiveLineStyles(
    accentCssVar: `--${string}`,
    backgroundPercent = 6,
    borderPercent = 30,
): ThemeStyleSpec {
    return {
        backgroundColor: `color-mix(in oklab, var(${accentCssVar}) ${backgroundPercent}%, transparent)`,
        borderLeft: `2px solid color-mix(in oklab, var(${accentCssVar}) ${borderPercent}%, transparent)`,
    };
}

export function statusLineStyles(
    accentCssVar: `--${string}`,
    options?: {
        backgroundPercent?: number;
        borderPercent?: number;
        animation?: string;
    },
): ThemeStyleSpec {
    return {
        backgroundColor: `color-mix(in oklab, var(${accentCssVar}) ${options?.backgroundPercent ?? 6}%, transparent) !important`,
        borderLeftColor: `color-mix(in oklab, var(${accentCssVar}) ${options?.borderPercent ?? 45}%, transparent) !important`,
        ...(options?.animation ? { animation: options.animation } : {}),
    };
}

export function hoverVariables(
    accentCssVar: `--${string}`,
    options?: {
        mutedPercent?: number;
        kindBackgroundPercent?: number;
        kindBorderPercent?: number;
        mixWith?: string;
    },
): ThemeStyleSpec {
    const mixWith = options?.mixWith ?? "var(--color-background)";
    return {
        "--cm-magic-hover-color": `var(${accentCssVar})`,
        "--cm-magic-hover-muted": `color-mix(in oklab, var(${accentCssVar}) ${options?.mutedPercent ?? 70}%, ${mixWith})`,
        "--cm-magic-hover-kind-color": `var(${accentCssVar})`,
        "--cm-magic-hover-kind-bg": `color-mix(in oklab, var(${accentCssVar}) ${options?.kindBackgroundPercent ?? 10}%, transparent)`,
        "--cm-magic-hover-kind-border": `color-mix(in oklab, var(${accentCssVar}) ${options?.kindBorderPercent ?? 18}%, transparent)`,
    };
}

export function directivePrettyStyles(
    accentCssVar: `--${string}`,
    options?: {
        gap?: string;
        fontSize?: string;
        cursor?: string;
        transition?: string;
        padding?: string;
        borderRadius?: string;
        backgroundPercent?: number;
        hoverBackgroundPercent?: number;
    },
): ThemeStyleSpec {
    const style: ThemeStyleSpec = {
        display: "inline-flex",
        alignItems: "center",
        gap: options?.gap ?? "4px",
        fontFamily: CM_WIDGET_FONT_FAMILY,
        fontSize: options?.fontSize ?? "13px",
        lineHeight: "1",
        verticalAlign: "baseline",
        ...hoverVariables(accentCssVar),
    };

    if (options?.cursor) style.cursor = options.cursor;
    if (options?.transition) style.transition = options.transition;
    if (options?.padding) style.padding = options.padding;
    if (options?.borderRadius) style.borderRadius = options.borderRadius;
    if (options?.backgroundPercent != null) {
        style.backgroundColor = `color-mix(in oklab, var(${accentCssVar}) ${options.backgroundPercent}%, transparent)`;
    }
    if (options?.hoverBackgroundPercent != null) {
        style["&:hover"] = {
            backgroundColor: `color-mix(in oklab, var(${accentCssVar}) ${options.hoverBackgroundPercent}%, transparent)`,
        };
    }

    return style;
}

export function accentIconStyles(
    accentCssVar: `--${string}`,
    foreground = "white",
): ThemeStyleSpec {
    return {
        "--cm-magic-syntax-badge-accent": `var(${accentCssVar})`,
        backgroundColor: `var(${accentCssVar})`,
        color: foreground,
    };
}

export function accentText(
    accentCssVar: `--${string}`,
    options?: {
        fontWeight?: string;
        fontSize?: string;
        letterSpacing?: string;
        fontFamily?: string;
        fontStyle?: string;
        opacity?: string;
    },
): ThemeStyleSpec {
    return {
        color: `var(${accentCssVar})`,
        ...(options?.fontWeight ? { fontWeight: options.fontWeight } : {}),
        ...(options?.fontSize ? { fontSize: options.fontSize } : {}),
        ...(options?.letterSpacing
            ? { letterSpacing: options.letterSpacing }
            : {}),
        ...(options?.fontFamily ? { fontFamily: options.fontFamily } : {}),
        ...(options?.fontStyle ? { fontStyle: options.fontStyle } : {}),
        ...(options?.opacity ? { opacity: options.opacity } : {}),
    };
}

export function accentMutedColor(
    accentCssVar: `--${string}`,
    percent = 60,
    mixWith = "transparent",
) {
    return `color-mix(in oklab, var(${accentCssVar}) ${percent}%, ${mixWith})`;
}

export function actionButtonStyles(
    accentCssVar: `--${string}`,
    options?: {
        borderSide?: "left" | "right";
    },
): ThemeStyleSpec {
    const style: ThemeStyleSpec = {
        color: `var(${accentCssVar})`,
        backgroundColor: `color-mix(in oklab, var(${accentCssVar}) 8%, transparent)`,
        "&:hover": {
            color: "white",
            backgroundColor: `var(${accentCssVar})`,
        },
    };

    if (options?.borderSide === "left") {
        style.borderLeft = `1px solid color-mix(in oklab, var(${accentCssVar}) 18%, transparent)`;
    }
    if (options?.borderSide === "right") {
        style.borderRight = `1px solid color-mix(in oklab, var(${accentCssVar}) 18%, transparent)`;
    }

    return style;
}

export function refMarkStyles(
    accentCssVar: `--${string}`,
    options?: {
        backgroundPercent?: number;
        borderPercent?: number;
        borderStyle?: "solid" | "dashed";
    },
): ThemeStyleSpec {
    return {
        color: `var(${accentCssVar})`,
        backgroundColor: `color-mix(in oklab, var(${accentCssVar}) ${options?.backgroundPercent ?? 6}%, transparent)`,
        borderBottom: `1px ${options?.borderStyle ?? "solid"} color-mix(in oklab, var(${accentCssVar}) ${options?.borderPercent ?? 40}%, transparent)`,
    };
}

export function refSourceVariables(
    accentCssVar: `--${string}`,
    primaryCssValue?: string,
): ThemeStyleSpec {
    return {
        ...hoverVariables(accentCssVar),
        "--cm-magic-ref-primary-color":
            primaryCssValue ?? `var(${accentCssVar})`,
    };
}
